import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
// Assuming you have a modal component or service
// import useModal from '@composable/useModal'; // Example

export default function useAuthTimeout(modalRef) {
    const store = useStore();
    const router = useRouter();
    // const { t } = useI18n(); // REMOVE
    // const { showConfirmationModal } = useModal(); // Example modal usage

    const warningTimeoutDuration = 30 * 1000; // 30 seconds for warning
    const finalTimeoutDuration = 45 * 1000; // 45 seconds total (warning + 15s grace)

    let warningTimer = null;
    let finalTimer = null;
    const showWarningModal = ref(false);

    const isRfidLogin = computed(() => store.getters['auth/getIsRfidLogin']);

    const resetTimers = () => {
        clearTimeout(warningTimer);
        clearTimeout(finalTimer);
        
        // Don't cancel the modal when it's actively showing the warning
        // Only close the modal through its normal button interactions or when finalTimer expires
        if (modalRef?.value?.isActive && !showWarningModal.value) {
            modalRef.value.cancel();
        }
        
        // Only reset the showWarningModal flag if it's not currently showing
        if (!showWarningModal.value) {
            if (isRfidLogin.value) {
                warningTimer = setTimeout(() => {
                    showWarningModal.value = true;
                }, warningTimeoutDuration);

                finalTimer = setTimeout(() => {
                    forceLogout();
                }, finalTimeoutDuration);
            }
        }
    };

    // Function to completely disable timeout
    const disableTimeout = () => {
        clearTimeout(warningTimer);
        clearTimeout(finalTimer);
        
        // Close modal if it's open
        if (modalRef?.value?.isActive) {
            modalRef.value.close();
        }
        
        showWarningModal.value = false;
        
        // Remove all event listeners to stop resetting timers
        window.removeEventListener('mousemove', resetTimers);
        window.removeEventListener('keypress', resetTimers);
        window.removeEventListener('click', resetTimers);
    };

    // Modified function with option to disable completely
    const extendSession = (disableCompletely = false) => {
        if (disableCompletely) {
            disableTimeout();
        } else {
            // First, explicitly set showWarningModal to false since the user responded
            showWarningModal.value = false;
            // Then reset the timers
            resetTimers();
        }
    };

    const forceLogout = async () => {
        modalRef?.value?.cancel();

        showWarningModal.value = false;
        clearTimeout(warningTimer);
        clearTimeout(finalTimer);

        // Set loading state to true for RFID logins, just like the header button does
        if (isRfidLogin.value) {
            store.commit('auth/SET_GLOBAL_LOADING', true);
        }

        let revalToken = null;
        let initialLogoutError = null;
        let sessionResumed = false;

        try {
            // 1. Get revalidation token BEFORE logging out
            revalToken = store.getters["auth/getRevalidationToken"];
            
            // 2. Dispatch logout (clears current RFID session state)
            await store.dispatch('auth/logout');
            
        } catch (logoutError) {
            // Handle failure of the initial logout itself
            console.error("[useAuthTimeout forceLogout] CAUGHT ERROR during initial logout dispatch:", logoutError);
            initialLogoutError = logoutError; // Store error to decide final redirect
        }

        // 3. If initial logout didn't fail AND we have a token, try resuming
        if (!initialLogoutError && revalToken) {
            try {
                await store.dispatch("auth/resumeSession", { revalidation_token: revalToken });
                await store.dispatch("auth/permissions");
                router.push({ name: 'RFIDScan' });
                sessionResumed = true; // Mark as resumed
            } catch (resumeError) {
                console.warn("[useAuthTimeout forceLogout] FAILED to resume session:", resumeError?.message || resumeError);
                // Proceed to fallback
            }
        }

        // 4. Final Fallback Redirect (only if session wasn't successfully resumed)
        if (!sessionResumed) {
            // If initial logout failed OR resume failed/skipped, where should we go?
            // Original timeout logic always went to RFIDScan.
            // But maybe if resume fails (meaning screen_view session is likely dead), go to SignIn?
            // Let's redirect to SignIn if resume failed or was skipped due to no token,
            // but stick to RFIDScan if only the initial logout failed (less common).
            if (initialLogoutError) {
                router.push({ name: 'RFIDScan' });
            } else {
                router.push({ name: 'SignIn' });
            }
        }
    };

    watch(isRfidLogin, (newValue) => {
        if (newValue) {
            resetTimers();
            window.addEventListener('mousemove', resetTimers);
            window.addEventListener('keypress', resetTimers);
            window.addEventListener('click', resetTimers);
        } else {
            clearTimeout(warningTimer);
            clearTimeout(finalTimer);
            if (modalRef?.value?.isActive) {
                modalRef.value.cancel();
            }
            showWarningModal.value = false;
            window.removeEventListener('mousemove', resetTimers);
            window.removeEventListener('keypress', resetTimers);
            window.removeEventListener('click', resetTimers);
        }
    }, { immediate: true });

    onUnmounted(() => {
        clearTimeout(warningTimer);
        clearTimeout(finalTimer);
        window.removeEventListener('mousemove', resetTimers);
        window.removeEventListener('keypress', resetTimers);
        window.removeEventListener('click', resetTimers);
    });

    return {
        showWarningModal,
        extendSession,
        forceLogout    
    };
} 